import { getWrestlerName, getWrestlerNameUpper } from './wrestlerUtils';
import { getPLELogo } from './plesDatabase';
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const trophyAsset = process.env.PUBLIC_URL + '/hof/trophy.png';
const hofBg = process.env.PUBLIC_URL + '/hof/hof_background.png';

const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.render) return w.render;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const GM_RENDERS_BASE = {
  'Adam Pearce': require('../assets/gm/94400_ADAM_PEARCE.png'),
  'Nick Aldis': require('../assets/gm/121300_NICK_ALDIS.png'),
  'Shawn Michaels': require('../assets/gm/45750_SHAWN_MICHAELS.png'),
  'Ava': require('../assets/gm/121400_AVA.png'),
  'Stephanie McMahon': require('../assets/gm/14700_STEPHANIE_MCMAHON.png'),
  'Paul Heyman': require('../assets/gm/37410_PAUL_HEYMAN_14.png'),
  'William Regal': require('../assets/gm/94010_WILLIAM_REGAL.png'),
  'Mick Foley': require('../assets/gm/14400_MICK_FOLEY.png'),
  'Teddy Long': require('../assets/gm/28600_TEDDY_LONG.png'),
  'Eric Bischoff': require('../assets/gm/93900_ERIC_BISCHOFF.png'),
  'Sensational Sherri': require('../assets/gm/42850_SENSATIONAL_SHERRI.png'),
  'Miss Elizabeth': require('../assets/gm/23450_MISS_ELIZABETH.png'),
  'Mr Fuji': require('../assets/gm/130250_MR_FUJI.png'),
  'Bobby Heenan': require('../assets/gm/24900_BOBBY_HENNAN.png'),
  'CM Punk': require('../assets/gm/31250_CM_PUNK.png'),
  'Xavier Woods': require('../assets/gm/42030_XAVIER_WOODS.png'),
  'Tyler Breeze': require('../assets/gm/58110_TYLER_BREEZE.png'),
  'Stacy Keibler': require('../assets/gm/148600_STACY_KEIBLER_GM.png'),
  'Anonymous GM': require('../assets/gm/149100_ANONYMOUS_GM.png'),
};

const BRAND_LOGOS = {
  'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
  'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
  'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
  'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
  'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
  'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
};

const BRAND_BACKGROUNDS = {
  'Raw': process.env.PUBLIC_URL + '/brands/backgrounds/RAW25_BACKGROUND.png',
  'SmackDown': process.env.PUBLIC_URL + '/brands/backgrounds/SMACKDOWN25_BACKGROUND.png',
  'NXT': process.env.PUBLIC_URL + '/brands/backgrounds/NXT_BACKGROUND.png',
  'NXT 2.0': process.env.PUBLIC_URL + '/brands/backgrounds/NXT20_BACKGROUND.png',
  'WCW': process.env.PUBLIC_URL + '/brands/backgrounds/WCW_BACKGROUND.png',
  'ECW': process.env.PUBLIC_URL + '/brands/backgrounds/ECW_BACKGROUND.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/backgrounds/NXTMUTINY_BACKGROUND.png',
};

const BRAND_COLORS = {
  'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#6c3483',
  'WCW': '#1a5276', 'ECW': '#7b241c', 'NXT 2.0': '#e91e8c', 'NXT Mutiny': '#424242',
};

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

const fmt = (v) => Math.round(Number(v || 0)).toLocaleString('fr-FR').replace(/,/g, ' ');
const fmtBudget = (v) => '$' + fmt(v);

// ── Compute career stats ─────────────────────────────────────────────
function computeCareerStats(gameData) {
  var seasonHistory = (gameData && gameData.seasonHistory) || [];
  var roster = (gameData && gameData.roster) || [];
  var titleHistory = (gameData && gameData.titleHistory) || [];
  var pastRivalries = (gameData && gameData.pastRivalries) || [];
  var initialBudget = (gameData && gameData.gameSettingsData && gameData.gameSettingsData.budget) || 3000000;
  var currentBudget = (gameData && gameData.budget) || 0;
  var currentFans = (gameData && gameData.fans) || 0;
  var season = (gameData && gameData.season) || 1;

  var drafted = roster.length;
  var freeAgentsSigned = roster.filter((w) => w.draftPick || w.signed).length;
  var rivsCompleted = pastRivalries.length;
  var injuries = seasonHistory.reduce((s, w) => s + ((w.injuries || []).length || 0), 0);
  var titleReigns = titleHistory.filter((e) =>
    (e.champions || []).some((name) =>
      roster.find((w) => String(w.wrestler_id) === String(name) || getWrestlerName(w, 'display') === name)
    )
  ).length;

  var allFans = seasonHistory.map((w) => w.fans || 0);
  var peakFans = allFans.length > 0 ? Math.max(...allFans) : currentFans;

  var allRatings = seasonHistory.map((w) => w.avgRating || 0).filter((r) => r > 0);
  var bestRating = allRatings.length > 0 ? Math.max(...allRatings) : 0;
  var avgRating = allRatings.length > 0 ? allRatings.reduce((s, r) => s + r, 0) / allRatings.length : 0;
  var champChanges = titleHistory.length;

  var pleWeeks = seasonHistory.filter((w) => w.weekType === 'ple' || w.weekType === 'wrestlemania');
  var pleWins = pleWeeks.filter((w) => {
    var matches = w.matches || [];
    var myWins = matches.filter((m) => m.winner && roster.find((r) => r.name === m.winner) && !m.noContest);
    var total = matches.filter((m) => !m.noContest).length;
    return total > 0 && myWins.length > total / 2;
  }).length;
  var pleLosses = pleWeeks.length - pleWins;

  var totalRevenue = seasonHistory.reduce((s, w) => s + (w.profits || 0), 0);
  var totalShowCost = seasonHistory.reduce((s, w) => s + (w.showCost || 0), 0);
  var netProfit = totalRevenue - totalShowCost;

  return {
    season,
    talent: { drafted, freeAgentsSigned, rivsCompleted, injuries, titleReigns },
    brand: { totalFans: currentFans, peakFans, bestRating: bestRating.toFixed(1), avgRating: avgRating.toFixed(1), champChanges, pleRecord: pleWins + 'W — ' + pleLosses + 'L' },
    money: { totalRevenue, totalShowCost, netProfit },
  };
}

// ── Compute highlights (random pick from pool per category) ──────────
function computeHighlights(gameData, allWrestlers) {
  var seasonHistory = (gameData && gameData.seasonHistory) || [];
  var roster = (gameData && gameData.roster) || [];
  var fullPool = allWrestlers && allWrestlers.length > 0 ? allWrestlers : roster;
  var pastRivalries = (gameData && gameData.pastRivalries) || [];
  var titleHistory = (gameData && gameData.titleHistory) || [];

  // Build per-wrestler stats — participants sont des strings (noms)
  var wrestlerStats = {};
  seasonHistory.forEach((week) => {
    (week.matches || []).forEach((m) => {
      var allParticipants = [];
      if (m.participants && m.participants.length > 0) {
        m.participants.filter(Boolean).forEach((p) => {
          allParticipants.push(typeof p === 'string' ? p : (p.name || getWrestlerName(p, 'display') || ''));
        });
      } else if (m.teams && m.teams.length > 0) {
        m.teams.forEach((t) => {
          var members = Array.isArray(t) ? t : (t.members || []);
          members.filter(Boolean).forEach((p) => {
            allParticipants.push(typeof p === 'string' ? p : (p.name || getWrestlerName(p, 'display') || ''));
          });
        });
      }
      allParticipants.forEach((name) => {
        if (!name || name === '?') return;
        if (!wrestlerStats[name]) wrestlerStats[name] = { bookings: 0, wins: 0, losses: 0, promos: 0 };
        wrestlerStats[name].bookings++;
        var winnerName = typeof m.winner === 'object'
          ? (m.winner && (m.winner.name || getWrestlerName(m.winner, 'display')))
          : m.winner;
        if (winnerName === name) wrestlerStats[name].wins++;
        else if (winnerName && winnerName !== name && !m.noContest) wrestlerStats[name].losses++;
      });
    });
    (week.promos || []).forEach((p) => {
      var name = p.wrestler || p.name;
      if (!name) return;
      if (!wrestlerStats[name]) wrestlerStats[name] = { bookings: 0, wins: 0, losses: 0, promos: 0 };
      wrestlerStats[name].promos = (wrestlerStats[name].promos || 0) + 1;
    });
  });

  // Lookup wrestler objet depuis le roster par toutes les variantes de nom
  var getRosterW = (name) => {
    if (!name) return null;
    var n = name.trim().toLowerCase();
    return fullPool.find((w) => {
      if (!w) return false;
      if (w.name && w.name.toLowerCase() === n) return true;
      var disp = (getWrestlerName(w, 'display') || '').toLowerCase();
      if (disp === n) return true;
      var full = (getWrestlerName(w, 'full') || '').toLowerCase();
      if (full === n) return true;
      if (w.last_name && w.first_name) {
        var combined = (w.first_name + ' ' + w.last_name).toLowerCase();
        if (combined === n) return true;
      }
      return false;
    }) || null;
  };

  // Most booked
  var mostBooked = Object.entries(wrestlerStats).sort((a, b) => b[1].bookings - a[1].bookings)[0];
  // Most wins
  var mostWins = Object.entries(wrestlerStats).sort((a, b) => b[1].wins - a[1].wins)[0];
  // Best W/L record (min 4 bookings)
  var bestRecord = Object.entries(wrestlerStats)
    .filter(([, s]) => s.bookings >= 4)
    .sort((a, b) => (b[1].wins / Math.max(b[1].bookings, 1)) - (a[1].wins / Math.max(a[1].bookings, 1)))[0];
  // Most promos
  var mostPromos = Object.entries(wrestlerStats).sort((a, b) => (b[1].promos || 0) - (a[1].promos || 0))[0];
  // Longest title reign
  var longestReign = null;
  var longestReignWeeks = 0;
  titleHistory.forEach((e) => {
    var dur = (e.endWeek || 0) - (e.startWeek || 0);
    if (dur > longestReignWeeks && e.champions && e.champions.length > 0) {
      longestReignWeeks = dur;
      longestReign = e;
    }
  });
  // Biggest rivalry
  var rivalryMatchCounts = {};
  pastRivalries.forEach((r) => {
    var key = (r.members || []).map((m) => m.name || m).sort().join('|');
    rivalryMatchCounts[key] = (rivalryMatchCounts[key] || 0) + (r.matches || 0);
  });
  var biggestRivalryKey = Object.entries(rivalryMatchCounts).sort((a, b) => b[1] - a[1])[0];
  // MVP: wins + bookings + rivalry wins
  var mvp = Object.entries(wrestlerStats)
    .map(([name, s]) => {
      var rivScore = pastRivalries.filter((r) => (r.members || []).some((m) => (m.name || m) === name) && r.winner === name).length;
      return { name, score: s.wins * 2 + s.bookings + rivScore * 3 };
    })
    .sort((a, b) => b.score - a.score)[0];

  // Best PLE
  var pleWeeks = seasonHistory.filter((w) => w.weekType === 'ple' || w.weekType === 'wrestlemania');
  var bestPLE = pleWeeks.sort((a, b) => (b.avgRating || 0) - (a.avgRating || 0))[0] || null;
  // Best week fans gained
  var bestWeekFans = seasonHistory
    .map((w, i) => ({ ...w, idx: i, gained: i > 0 ? (w.fans || 0) - (seasonHistory[i - 1].fans || 0) : 0 }))
    .sort((a, b) => b.gained - a.gained)[0] || null;
  // Worst week fans
  var worstWeekFans = seasonHistory
    .map((w, i) => ({ ...w, idx: i, gained: i > 0 ? (w.fans || 0) - (seasonHistory[i - 1].fans || 0) : 0 }))
    .sort((a, b) => a.gained - b.gained)[0] || null;
  // Most used match type (hors Normal)
  var matchTypeCounts = {};
  seasonHistory.forEach((w) => {
    (w.matches || []).forEach((m) => {
      var rule = m.rule || m.matchType || 'Normal';
      if (rule === 'Normal' || rule === 'normal') return;
      matchTypeCounts[rule] = (matchTypeCounts[rule] || 0) + 1;
    });
  });
  var mostUsedMatchType = Object.entries(matchTypeCounts).sort((a, b) => b[1] - a[1])[0] || null;
  // Fan growth
  var fanStart = seasonHistory.length > 0 ? (seasonHistory[0].fans || 0) : 0;
  var fanEnd = (gameData && gameData.fans) || 0;
  var fanGrowth = fanEnd - fanStart;
  // Most profitable PLE
  var mostProfitablePLE = pleWeeks.sort((a, b) => (b.profits || 0) - (a.profits || 0))[0] || null;
  // Most profitable week
  var mostProfitableWeek = seasonHistory.sort((a, b) => (b.profits || 0) - (a.profits || 0))[0] || null;
  // Worst week money
  var worstWeekMoney = seasonHistory.sort((a, b) => (a.profits || 0) - (b.profits || 0))[0] || null;
  // Most expensive match
  var mostExpensiveMatch = null;
  var maxMatchCost = 0;
  seasonHistory.forEach((w) => {
    (w.matches || []).forEach((m) => {
      if ((m.cost || 0) > maxMatchCost) { maxMatchCost = m.cost; mostExpensiveMatch = m; }
    });
  });
  // Biggest signing
  var biggestSigning = roster.slice().sort((a, b) => (b.contractValue || b.salary || 0) - (a.contractValue || a.salary || 0))[0] || null;
  // Most expensive booking week
  var mostExpensiveWeek = seasonHistory
    .map((w) => ({ ...w, totalSpent: (w.showCost || 0) }))
    .sort((a, b) => b.totalSpent - a.totalSpent)[0] || null;

  // Pools
  var talentPool = [
    mostBooked && {
      id: 'mostBooked', label: 'MOST BOOKED', wrestler: mostBooked[0],
      wrestlerObj: getRosterW(mostBooked[0]),
      main: mostBooked[1].bookings + ' MATCHS',
      sub: mostBooked[1].wins + 'W / ' + mostBooked[1].losses + 'L',
    },
    mostWins && {
      id: 'mostWins', label: 'MOST WINS', wrestler: mostWins[0],
      wrestlerObj: getRosterW(mostWins[0]),
      main: mostWins[1].wins + ' WINS',
      sub: mostWins[1].bookings + ' MATCHS',
    },
    bestRecord && {
      id: 'bestRecord', label: 'BEST W/L RECORD', wrestler: bestRecord[0],
      wrestlerObj: getRosterW(bestRecord[0]),
      main: bestRecord[1].wins + 'W / ' + bestRecord[1].losses + 'L',
      sub: bestRecord[1].bookings + ' MATCHS',
    },
    mostPromos && mostPromos[1].promos > 0 && {
      id: 'mostPromos', label: 'MOST PROMOS', wrestler: mostPromos[0],
      wrestlerObj: getRosterW(mostPromos[0]),
      main: mostPromos[1].promos + ' PROMOS',
      sub: mostPromos[1].bookings + ' BOOKINGS',
    },
    longestReign && {
      id: 'longestReign', label: 'LONGEST REIGN', wrestler: longestReign.champions[0],
      wrestlerObj: getRosterW(longestReign.champions[0]),
      main: longestReignWeeks + ' WEEKS',
      sub: longestReign.title || 'CHAMPIONSHIP',
    },
    biggestRivalryKey && {
      id: 'biggestRivalry', label: 'BIGGEST RIVALRY',
      wrestler: biggestRivalryKey[0].split('|')[0],
      wrestlerObj: getRosterW(biggestRivalryKey[0].split('|')[0]),
      wrestlerB: biggestRivalryKey[0].split('|')[1],
      wrestlerObjB: getRosterW(biggestRivalryKey[0].split('|')[1]),
      main: biggestRivalryKey[1] + ' MATCHS',
      sub: 'HEAD TO HEAD',
    },
    mvp && {
      id: 'mvp', label: 'MVP OF THE SEASON', wrestler: mvp.name,
      wrestlerObj: getRosterW(mvp.name),
      main: wrestlerStats[mvp.name] ? wrestlerStats[mvp.name].wins + ' WINS' : '—',
      sub: wrestlerStats[mvp.name] ? wrestlerStats[mvp.name].bookings + ' BOOKINGS' : '—',
    },
  ].filter(Boolean);

  var brandPool = [
    bestPLE && {
      id: 'bestPLE', label: 'BEST PLE', isPLE: true,
      pleName: bestPLE.pleName || bestPLE.ple || 'PLE',
      pleLogo: bestPLE.ple ? getPLELogo(bestPLE.ple) : (bestPLE.pleLogo || null),
      main: '+' + fmt(bestPLE.fanGain || 0) + ' FANS',
      sub: 'NOTE ' + (bestPLE.avgRating || '—') + ' ★',
    },
    bestWeekFans && {
      id: 'bestWeekFans', label: 'BEST WEEK', isWeek: true,
      weekNum: bestWeekFans.week || '—',
      weekType: bestWeekFans.weekType === 'ple' ? 'GO-HOME SHOW' : 'REGULAR SHOW',
      main: '+' + fmt(bestWeekFans.gained) + ' FANS',
      sub: 'NOTE ' + (bestWeekFans.avgRating || '—') + ' ★',
    },
    worstWeekFans && worstWeekFans.gained < 0 && {
      id: 'worstWeek', label: 'WORST WEEK', isWeek: true,
      weekNum: worstWeekFans.week || '—',
      weekType: 'DARKEST MOMENT',
      main: fmt(worstWeekFans.gained) + ' FANS',
      sub: 'NOTE ' + (worstWeekFans.avgRating || '—') + ' ★',
    },
    mostUsedMatchType && {
      id: 'matchType', label: 'MOST USED MATCH TYPE', isText: true,
      main: mostUsedMatchType[0].toUpperCase(),
      sub: mostUsedMatchType[1] + ' TIMES',
    },
    { id: 'fanGrowth', label: 'FAN GROWTH', isText: true, main: (fanGrowth >= 0 ? '+' : '') + fmt(fanGrowth) + ' FANS', sub: 'OVER THE SEASON' },
  ].filter(Boolean);

  var moneyPool = [
    mostProfitablePLE && {
      id: 'profitablePLE', label: 'MOST PROFITABLE PLE', isPLE: true,
      pleName: mostProfitablePLE.pleName || mostProfitablePLE.ple || 'PLE',
      pleLogo: mostProfitablePLE.ple ? getPLELogo(mostProfitablePLE.ple) : (mostProfitablePLE.pleLogo || null),
      main: fmtBudget(mostProfitablePLE.profits || 0),
      sub: 'PROFITS',
    },
    mostProfitableWeek && {
      id: 'profitableWeek', label: 'BEST WEEK', isWeek: true,
      weekNum: mostProfitableWeek.week || '—',
      weekType: 'PROFITS',
      main: fmtBudget(mostProfitableWeek.profits || 0),
      sub: 'WEEK ' + (mostProfitableWeek.week || '—'),
    },
    worstWeekMoney && {
      id: 'worstMoney', label: 'WORST WEEK', isWeek: true,
      weekNum: worstWeekMoney.week || '—',
      weekType: 'LOSSES',
      main: fmtBudget(worstWeekMoney.profits || 0),
      sub: 'WEEK ' + (worstWeekMoney.week || '—'),
    },
    biggestSigning && {
      id: 'biggestSigning', label: 'BIGGEST SIGNING',
      wrestler: getWrestlerName(biggestSigning, 'display'),
      wrestlerObj: biggestSigning,
      main: fmtBudget(biggestSigning.contractValue || biggestSigning.salary || 0),
      sub: (biggestSigning.contractDuration || biggestSigning.contractEnd || '—') + ' WEEKS',
    },
    mostExpensiveWeek && mostExpensiveWeek.showCost > 0 && {
      id: 'expensiveWeek', label: 'BIGGEST SPENDING WEEK', isWeek: true,
      weekNum: mostExpensiveWeek.week || '—',
      weekType: 'TOTAL SPENT',
      main: fmtBudget(mostExpensiveWeek.showCost),
      sub: 'WEEK ' + (mostExpensiveWeek.week || '—'),
    },
  ].filter(Boolean);

  // Random pick 3 from each pool (seeded by season number for consistency per session)
  var seed = (gameData && gameData.season) || 1;
  var pickRandom = (pool) => {
    var arr = [...pool];
    var picked = [];
    var s = seed * 137 + 42;
    while (picked.length < 3 && arr.length > 0) {
      s = (s * 1664525 + 1013904223) & 0xffffffff;
      var idx = Math.abs(s) % arr.length;
      picked.push(arr.splice(idx, 1)[0]);
    }
    return picked;
  };

  return {
    talent: pickRandom(talentPool),
    brand: pickRandom(brandPool),
    money: pickRandom(moneyPool),
  };
}

// ── HOF Background ───────────────────────────────────────────────────
const HofBackground = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
    <img src={hofBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.40 }} onError={(e) => { e.target.style.display = 'none'; }} />
    <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 40%, rgba(212,175,55,0.06) 0%, transparent 55%), #050402' }} />
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
  </div>
);

// ── Wrestler mini card ───────────────────────────────────────────────
const WrestlerMini = ({ wrestlerObj, name }) => {
  var render = wrestlerObj ? getWrestlerRender(wrestlerObj) : null;
  var displayName = wrestlerObj ? getWrestlerNameUpper(wrestlerObj) : (name || '').toUpperCase();
  return (
    <div style={{ width: '62px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '62px', height: '78px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
        {render
          ? <img loading="lazy" src={render} alt="" style={{ position: 'absolute', bottom: '-12px', left: '50%', transform: 'translateX(-50%)', height: '100px', objectFit: 'contain', objectPosition: 'bottom' }} onError={(e) => { e.target.style.display = 'none'; }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: 'rgba(255,255,255,0.15)' }}>?</div>
        }
      </div>
    </div>
  );
};

// ── PLE mini visual ──────────────────────────────────────────────────
const PLEMini = ({ pleName, pleLogo }) => (
  <div style={{ width: '72px', height: '56px', flexShrink: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {pleLogo
      ? <img loading="lazy" src={pleLogo} alt={pleName} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} onError={(e) => { e.target.style.display = 'none'; }} />
      : <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px', textAlign: 'center', padding: '4px' }}>{(pleName || 'PLE').toUpperCase()}</div>
    }
  </div>
);

// ── Week badge ──────────────────────────────────────────────────────
const WeekBadge = ({ weekNum }) => (
  <div style={{ width: '56px', height: '56px', flexShrink: 0, background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.22)', borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '9px', color: 'rgba(52,152,219,0.6)', letterSpacing: '1px' }}>WEEK</div>
    <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '20px', color: '#3498db', letterSpacing: '1px', lineHeight: 1 }}>{weekNum}</div>
  </div>
);

// ── Highlight card ──────────────────────────────────────────────────
const HighlightCard = ({ item }) => {
  if (!item) return null;
  return (
    <div style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: '6px', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)' }} />
      <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(212,175,55,0.65)', padding: '7px 12px 5px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px' }}>
        {item.wrestler && <WrestlerMini wrestlerObj={item.wrestlerObj} name={item.wrestler} />}
        {item.wrestlerB && <WrestlerMini wrestlerObj={item.wrestlerObjB} name={item.wrestlerB} />}
        {item.isPLE && <PLEMini pleName={item.pleName} pleLogo={item.pleLogo} />}
        {item.isWeek && <WeekBadge weekNum={item.weekNum} />}
        {item.isText && (
          <div style={{ width: '56px', height: '56px', flexShrink: 0, background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '22px', color: '#d4af37' }}>★</div>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {item.isPLE && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(item.pleName || '').toUpperCase()}</div>}
          {item.isWeek && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '3px' }}>{item.weekType}</div>}
          {item.isText && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '3px' }}>{item.sub}</div>}
          {item.wrestler && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.wrestler.toUpperCase()}</div>}
          <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '18px', color: '#fff', letterSpacing: '1px', lineHeight: 1.1 }}>{item.main}</div>
          {!item.isText && !item.wrestler && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '3px' }}>{item.sub}</div>}
          {item.wrestler && <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '3px' }}>{item.sub}</div>}
        </div>
      </div>
    </div>
  );
};

// ── Stat column ──────────────────────────────────────────────────────
const StatColumn = ({ title, generalRows, highlights, colIndex }) => {
  var baseDelay = (colIndex || 0) * 120;
  return (
  <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(212,175,55,0.08)', overflow: 'hidden', flex: 1, minHeight: 0 }}>
    <div className="hof-card-reveal" style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(212,175,55,0.18)', flexShrink: 0, animationDelay: baseDelay + 'ms' }}>
      <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '13px', letterSpacing: '2px', color: '#d4af37' }}>{title}</div>
    </div>
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* General card */}
      <div className="hof-card-reveal" style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '6px', padding: '10px 12px', position: 'relative', flexShrink: 0, animationDelay: (baseDelay + 80) + 'ms' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)' }} />
        {generalRows.map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < generalRows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
            <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.38)' }}>{row.label}</div>
            <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '13px', color: row.color || '#d4af37', letterSpacing: '0.5px' }}>{row.value}</div>
          </div>
        ))}
      </div>
      {/* Highlights avec délai croissant */}
      {highlights.map((item, i) => (
        <div key={i} className="hof-card-reveal" style={{ animationDelay: (baseDelay + 200 + i * 100) + 'ms' }}>
          <HighlightCard item={item} />
        </div>
      ))}
    </div>
  </div>
  );
};

// ── Standing row ─────────────────────────────────────────────────────
const StandRow = ({ entry, rank, isFirst }) => {
  var bColor = BRAND_COLORS[entry.brand] || entry.color || '#888';
  var bLogo = BRAND_LOGOS[entry.brand] || entry.logo || null;
  var bBg = BRAND_BACKGROUNDS[entry.brand] || entry.brandBg || null;
  var gmImg = GM_RENDERS_BASE[entry.gm] || entry.gmRender || null;
  var rankColors = ['#d4af37', '#95a5a6', '#e67e22', '#888888'];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 10px', borderRadius: '6px', position: 'relative', overflow: 'hidden', border: '1px solid ' + (isFirst ? bColor + '66' : 'rgba(255,255,255,0.06)'), boxShadow: isFirst ? '0 0 14px ' + bColor + '33' : 'none', minHeight: '60px' }}>
      {bBg && <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(' + bBg + ')', backgroundSize: 'cover', backgroundPosition: 'center right', opacity: isFirst ? 0.22 : 0.08 }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 100%)' }} />
      {/* Rank */}
      <div style={{ ...S.anton, fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0, color: rankColors[rank] || '#888', position: 'relative', zIndex: 1 }}>{rank + 1}</div>
      {/* GM render — PNG transparent, déborde vers le haut */}
      <div style={{ width: '38px', height: '56px', flexShrink: 0, position: 'relative', zIndex: 1, overflow: 'visible' }}>
        {gmImg && (
          <img loading="lazy" src={gmImg} alt="" style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', height: '72px', objectFit: 'contain', objectPosition: 'bottom', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.95))' }} onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </div>
      {/* Info : brand logo + noms */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
        {bLogo
          ? <img loading="lazy" src={bLogo} alt={entry.brand} style={{ maxWidth: '54px', maxHeight: '22px', objectFit: 'contain', display: 'block', marginBottom: '3px', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }} onError={(e) => { e.target.style.display = 'none'; }} />
          : <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '3px' }}>{entry.brand}</div>
        }
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.gm}</div>
      </div>
      {/* Stats */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '13px', color: isFirst ? '#d4af37' : 'rgba(255,255,255,0.45)', letterSpacing: '0.5px' }}>{fmt(entry.fans)}</div>
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '10px', color: 'rgba(212,175,55,0.6)' }}>🏆 {entry.hofCount || 0}</div>
      </div>
    </div>
  );
};

// ── MAIN ─────────────────────────────────────────────────────────────
export default function HallOfFameSequence({ gameData, onClose }) {
  var myBrand = (gameData && gameData.brand) || '';
  var myGm = (gameData && gameData.gm) || '';
  var myFans = (gameData && gameData.fans) || 0;
  var myBudget = (gameData && gameData.budget) || 0;
  var brandColor = (gameData && gameData.brandColor) || BRAND_COLORS[myBrand] || '#d4af37';
  var rivals = ((gameData && gameData.rivals) || []).filter(Boolean);
  var victoryCriteria = (gameData && gameData.gameSettingsData && gameData.gameSettingsData.victoryCriteria) || 'fans';
  var gmRender = GM_RENDERS_BASE[myGm] || (gameData && gameData.gmRender) || null;
  var brandLogo = BRAND_LOGOS[myBrand] || (gameData && gameData.brandLogo) || null;
  var brandBgImg = BRAND_BACKGROUNDS[myBrand] || (gameData && gameData.brandBg) || null;

  // HOF counts
  var AUTO_FAN_THRESHOLDS = [2300000, 3400000, 4500000, 5250000, 6000000];
  var AUTO_PROFIT_THRESHOLDS = [2000000, 5250000, 8750000, 12500000, 16000000];
  var myHofActive = (gameData && gameData.hofActive) || [];
  var myTotalProfits = ((gameData && gameData.seasonHistory) || []).reduce((s, w) => s + (w.profits || 0), 0);
  var myAutoFan = AUTO_FAN_THRESHOLDS.filter((t) => myFans >= t).length;
  var myAutoProfit = AUTO_PROFIT_THRESHOLDS.filter((t) => myTotalProfits >= t).length;
  var myHofCount = myHofActive.length + myAutoFan + myAutoProfit;

  var calcRivalHof = (r) => {
    var rFans = r.fans || 0;
    var rProfits = r.totalProfits || 0;
    var rAutoFan = AUTO_FAN_THRESHOLDS.filter((t) => rFans >= t).length;
    var rAutoProfit = AUTO_PROFIT_THRESHOLDS.filter((t) => rProfits >= t).length;
    return (r.hofActive || []).length + rAutoFan + rAutoProfit;
  };

  // Build ranking
  var entries = [
    { brand: myBrand, gm: myGm, fans: myFans, hofCount: myHofCount, color: brandColor, logo: brandLogo, brandBg: BRAND_BACKGROUNDS[myBrand] || (gameData && gameData.brandBgFull) || null, gmRender: gmRender, isPlayer: true },
    ...rivals.map((r) => ({
      brand: r.brand, gm: r.gm || '', fans: r.fans || 0,
      hofCount: calcRivalHof(r),
      color: BRAND_COLORS[r.brand] || r.brandColor || '#888',
      logo: BRAND_LOGOS[r.brand] || r.brandLogo || null,
      brandBg: BRAND_BACKGROUNDS[r.brand] || r.brandBg || null,
      gmRender: GM_RENDERS_BASE[r.gm] || r.gmRender || null,
      isPlayer: false,
    })),
  ].sort((a, b) => {
    if (victoryCriteria === 'hof') return b.hofCount !== a.hofCount ? b.hofCount - a.hofCount : b.fans - a.fans;
    return b.fans !== a.fans ? b.fans - a.fans : b.hofCount - a.hofCount;
  });

  // Phases: intro → winner
  var [phase, setPhase] = useState('intro');
  var [allWrestlers, setAllWrestlers] = useState([]);

  useEffect(() => {
    var t = setTimeout(() => setPhase('winner'), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    var gameVersion = (gameData && gameData.gameSettingsData && gameData.gameSettingsData.gameVersion) || '2k26';
    var collName = gameVersion === '2k25' ? 'wrestlers_2k25' : 'wrestlers_2k26';
    getDocs(collection(db, collName)).then((snap) => {
      setAllWrestlers(snap.docs.map((d) => d.data()));
    }).catch(() => {});
  }, []);

  // Stats + highlights — highlights se recalcule quand allWrestlers arrive
  var stats = computeCareerStats(gameData);
  var highlights = computeHighlights(gameData, allWrestlers);

  var css = `
    .hof-root * { box-sizing: border-box; }
    .hof-root ::-webkit-scrollbar { width: 4px; }
    .hof-root ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }
    .hof-btn { transition: filter 0.2s, transform 0.2s; cursor: pointer; }
    .hof-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }
    @keyframes fadeInDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes shimmer { 0%,100% { text-shadow: 0 0 20px rgba(212,175,55,0.3); } 50% { text-shadow: 0 0 40px rgba(212,175,55,0.7), 0 0 80px rgba(212,175,55,0.3); } }
    @keyframes scaleReveal { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
    .hof-title { animation: fadeInDown 1s ease forwards, shimmer 3s ease 1s infinite; }
    .hof-fade { animation: fadeIn 0.7s ease forwards; }
    .hof-card-reveal { animation: scaleReveal 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
  `;

  return (
    <div className="hof-root" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 999, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{css}</style>
      <HofBackground />

      {/* ── INTRO ── */}
      {phase === 'intro' && (
        <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <img loading="lazy" src={trophyAsset} alt="" style={{ width: '80px', height: '80px', objectFit: 'contain', filter: 'drop-shadow(0 0 24px rgba(212,175,55,0.6))', animation: 'fadeIn 0.8s ease forwards' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="hof-title" style={{ ...S.anton, fontSize: '4rem', letterSpacing: '8px', color: '#d4af37', textAlign: 'center' }}>HALL OF FAME</div>
          <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '4px', color: 'rgba(255,255,255,0.4)', animation: 'fadeIn 1.2s ease 0.4s forwards', opacity: 0 }}>END OF SEASON · FINAL STANDINGS</div>
        </div>
      )}

      {/* ── WINNER + STATS ── */}
      {phase === 'winner' && (
        <div className="hof-fade" style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

          {/* LEFT COL */}
          <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(212,175,55,0.15)', overflow: 'hidden' }}>

            {/* GM Zone */}
            <div style={{ height: '220px', flexShrink: 0, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              {/* Brand BG image */}
              {brandBgImg && <img loading="lazy" src={brandBgImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.75 }} onError={(e) => { e.target.style.display = 'none'; }} />}
              {/* Dark gradient overlay bottom */}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)' }} />
              {/* GM render */}
              {gmRender && (
                <img loading="lazy" src={gmRender} alt="" style={{ position: 'relative', zIndex: 1, height: '260px', width: 'auto', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.95))', marginBottom: '-10px' }} onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              {/* Brand logo bottom overlay */}
              <div style={{ position: 'absolute', bottom: '10px', left: 0, right: 0, zIndex: 2, display: 'flex', justifyContent: 'center' }}>
                {brandLogo && <img loading="lazy" src={brandLogo} alt={myBrand} style={{ height: '44px', objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.95))' }} onError={(e) => { e.target.style.display = 'none'; }} />}
              </div>
            </div>

            {/* Winner info */}
            <div style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.8)', borderTop: '2px solid ' + brandColor, borderBottom: '1px solid rgba(212,175,55,0.1)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#d4af37', flexShrink: 0 }} />
                <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '3px', color: 'rgba(212,175,55,0.7)' }}>WINNER</div>
              </div>
              <div style={{ ...S.anton, fontSize: '22px', letterSpacing: '2px', color: '#d4af37', lineHeight: 1 }}>{myGm ? myGm.toUpperCase() : myBrand.toUpperCase()}</div>
              <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '10px', marginTop: '3px' }}>{myBrand} GENERAL MANAGER</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>FANS</span>
                  <span style={{ ...S.anton, fontSize: '16px', color: '#fff' }}>{fmt(myFans)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>HOF TROPHIES</span>
                  <span style={{ ...S.anton, fontSize: '16px', color: '#d4af37' }}>{myHofCount}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>BUDGET</span>
                  <span style={{ ...S.anton, fontSize: '16px', color: '#2ecc71' }}>{fmtBudget(myBudget)}</span>
                </div>
              </div>
            </div>

            {/* Standing */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.2)', marginBottom: '4px' }}>FINAL STANDING</div>
              {entries.map((entry, i) => (
                <StandRow key={i} entry={entry} rank={i} isFirst={i === 0} />
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(212,175,55,0.1)', flexShrink: 0 }}>
              <button className="hof-btn" onClick={onClose} style={{ width: '100%', ...S.anton, fontSize: '13px', letterSpacing: '2px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.45)', color: '#d4af37', padding: '10px', borderRadius: '5px' }}>
                CONTINUE →
              </button>
            </div>
          </div>

          {/* RIGHT: 3 stat columns */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid rgba(212,175,55,0.1)', flexShrink: 0, display: 'flex', alignItems: 'baseline', gap: '14px' }}>
              <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '3px', color: 'rgba(212,175,55,0.5)' }}>SEASON {stats.season} RECAP</div>
              <div style={{ ...S.anton, fontSize: '22px', letterSpacing: '3px', color: '#fff' }}>YOUR GM CAREER STATS</div>
            </div>
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              <StatColumn
                colIndex={0}
                title="TALENT MANAGEMENT"
                generalRows={[
                  { label: 'Superstars Drafted', value: stats.talent.drafted },
                  { label: 'Free Agents Signed', value: stats.talent.freeAgentsSigned },
                  { label: 'Rivalries Completed', value: stats.talent.rivsCompleted, color: '#9b59b6' },
                  { label: 'Injuries', value: stats.talent.injuries, color: '#e74c3c' },
                  { label: 'Title Reigns', value: stats.talent.titleReigns },
                ]}
                highlights={highlights.talent}
              />
              <StatColumn
                colIndex={1}
                title="BRAND PERFORMANCE"
                generalRows={[
                  { label: 'Total Fans', value: fmt(stats.brand.totalFans), color: '#3498db' },
                  { label: 'Peak Fans', value: fmt(stats.brand.peakFans), color: '#3498db' },
                  { label: 'Best Show Rating', value: stats.brand.bestRating + ' ★', color: '#f39c12' },
                  { label: 'Avg Show Rating', value: stats.brand.avgRating + ' ★', color: '#f39c12' },
                  { label: 'Championship Changes', value: stats.brand.champChanges },
                  { label: 'PLE Record', value: stats.brand.pleRecord, color: '#9b59b6' },
                ]}
                highlights={highlights.brand}
              />
              <StatColumn
                colIndex={2}
                title="MONEY MANAGEMENT"
                generalRows={[
                  { label: 'Total Revenue', value: fmtBudget(stats.money.totalRevenue), color: '#2ecc71' },
                  { label: 'Spent on Shows', value: fmtBudget(stats.money.totalShowCost), color: '#e74c3c' },
                  { label: 'Net Profit', value: fmtBudget(stats.money.netProfit), color: stats.money.netProfit >= 0 ? '#2ecc71' : '#e74c3c' },
                ]}
                highlights={highlights.money}
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
